import { useState, useEffect, useRef } from 'react'
import { getApiUrl } from '../../api'

declare global {
  interface Window {
    naver: any
  }
}

interface CompanyInfo {
  company_name: string | null
  zipcode: string | null
  address: string | null
  phone: string | null
  email: string | null
  operating_hours: string | null
}

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토']

// 0=일 ~ 6=토 값을 월~일 순서로 정렬
const sortWeekdays = (weekdays: number[]) =>
  [...weekdays].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7))

const FALLBACK_LAT = 36.7969
const FALLBACK_LNG = 127.0985

export default function Location() {
  const mapRef = useRef<HTMLDivElement>(null)
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null)
  const [closedWeekdays, setClosedWeekdays] = useState<number[] | null>(null)
  const [autoPublicHoliday, setAutoPublicHoliday] = useState(false)

  useEffect(() => {
    fetch(getApiUrl('/marubit/api/company/detail'))
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setCompanyInfo(data) })
      .catch(() => {})
  }, [])

  useEffect(() => {
    fetch(getApiUrl('/marubit/api/holiday/weekdays'))
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setClosedWeekdays(data.closed_weekdays) })
      .catch(() => {})
  }, [])

  useEffect(() => {
    fetch(getApiUrl('/marubit/api/holiday/setting'))
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data) setAutoPublicHoliday(data.auto_public_holiday) })
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (!companyInfo) return

    const initMap = () => {
      if (!mapRef.current || !window.naver) return

    const renderMap = (lat: number, lng: number) => {
    const center = new window.naver.maps.LatLng(lat, lng)
    const map = new window.naver.maps.Map(mapRef.current, {
        center,
        zoom: 16,
        zoomControl: true,
        zoomControlOptions: {
        position: window.naver.maps.Position.TOP_RIGHT,
        },
    })
    const marker = new window.naver.maps.Marker({
        position: center,
        map,
    })

    if (companyInfo.company_name) {
        const infoWindow = new window.naver.maps.InfoWindow({
        content: `<div style="padding:8px 12px;font-size:13px;font-weight:600;color:#222;white-space:nowrap;">${companyInfo.company_name}</div>`,
        borderWidth: 1,
        borderColor: '#e8e0dc',
        anchorSize: new window.naver.maps.Size(10, 10),
        pixelOffset: new window.naver.maps.Point(0, -10),
        })
        infoWindow.open(map, marker)
    }
    }

      // 주소가 있으면 geocoding으로 좌표 변환, 없으면 fallback 좌표 사용
      if (companyInfo.address && window.naver.maps.Service) {
        window.naver.maps.Service.geocode(
          { query: companyInfo.address },
          (status: any, response: any) => {
            if (
              status === window.naver.maps.Service.Status.OK &&
              response.v2.addresses.length > 0
            ) {
              const result = response.v2.addresses[0]
              renderMap(parseFloat(result.y), parseFloat(result.x))
            } else {
              renderMap(FALLBACK_LAT, FALLBACK_LNG)
            }
          }
        )
      } else {
        renderMap(FALLBACK_LAT, FALLBACK_LNG)
      }
    }

    if (window.naver?.maps) {
      initMap()
    } else {
      const interval = setInterval(() => {
        if (window.naver?.maps) {
          clearInterval(interval)
          initMap()
        }
      }, 200)
      return () => clearInterval(interval)
    }
  }, [companyInfo])  // companyInfo 로드 후 지도 초기화

  const fullAddress = companyInfo
    ? [companyInfo.zipcode ? `(${companyInfo.zipcode})` : null, companyInfo.address]
        .filter(Boolean)
        .join(' ')
    : null

  const InfoRow = ({
    icon,
    label,
    value,
  }: {
    icon: React.ReactNode
    label: string
    value: string | null
  }) => {
    if (!value) return null
    return (
      <div className="flex items-start gap-4 py-4 border-b border-gray-100 last:border-none">
        <div className="w-8 h-8 rounded-full bg-[#f5f2f0] flex items-center justify-center shrink-0 mt-[1px]">
          {icon}
        </div>
        <div>
          <p className="text-[12px] text-gray-400 font-medium mb-[2px]">{label}</p>
          <p className="text-[14px] text-[#222] font-medium whitespace-pre-line">{value}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 타이틀 */}
        <div className="mb-8">
          <h1 className="text-[26px] font-extrabold text-[#5a4035] mb-2">오시는 길</h1>
          {fullAddress && <p className="text-[14px] text-gray-400">{fullAddress}</p>}
        </div>

        {/* 지도 */}
        <div className="rounded-2xl overflow-hidden mb-6 shadow-[0_2px_12px_rgba(136,114,103,0.10)]">
          <div ref={mapRef} style={{ width: '100%', height: '420px' }} />
        </div>

        {/* 정보 카드 */}
        <div className="bg-white rounded-2xl p-7 shadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          {companyInfo?.company_name && (
            <h2 className="text-[16px] font-bold text-[#5a4035] border-l-[3px] border-[#887267] pl-[10px] mb-2">
              {companyInfo.company_name}
            </h2>
          )}
          <InfoRow
            icon={
              <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#887267" strokeWidth={2}>
                <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z" />
                <circle cx="12" cy="10" r="3" />
              </svg>
            }
            label="주소"
            value={fullAddress}
          />
          <InfoRow
            icon={
              <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#887267" strokeWidth={2}>
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.22h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.8a16 16 0 0 0 6.29 6.29l.96-.96a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" />
              </svg>
            }
            label="전화번호"
            value={companyInfo?.phone ?? null}
          />
          <InfoRow
            icon={
              <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#887267" strokeWidth={2}>
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                <polyline points="22,6 12,13 2,6" />
              </svg>
            }
            label="이메일"
            value={companyInfo?.email ?? null}
          />
          <InfoRow
            icon={
              <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#887267" strokeWidth={2}>
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
            }
            label="이용시간"
            value={companyInfo?.operating_hours ?? null}
          />
          <InfoRow
            icon={
              <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="#887267" strokeWidth={2}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            }
            label="휴무일"
            value={
              closedWeekdays === null
                ? null
                : closedWeekdays.length > 0 || autoPublicHoliday
                  ? `${[
                      closedWeekdays.length > 0
                        ? `매주 ${sortWeekdays(closedWeekdays).map(w => WEEKDAY_LABELS[w]).join('·')}요일`
                        : null,
                      autoPublicHoliday ? '공휴일' : null,
                    ].filter(Boolean).join(', ')} 휴무`
                  : '연중무휴'
            }
          />
        </div>
      </div>
    </div>
  )
}